import { SetStateAction, MouseEvent, Dispatch as DispatchReact, memo, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { useHistory } from 'react-router';

import { Dropdown } from 'UIPackage/MainModule/components/Dropdown';
import { DropdownItem } from 'UIPackage/MainModule/components/DropdownItem';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';

import { PassportTabsEnum } from '../enums/PassportTabsEnum';
import styles from './KnmHeader.module.scss';
import { knmPreviewHeaderTabs } from '../constants/knmPreviewHeaderTabs';
import { erknmPreviewHeaderTabs } from '../constants/erknmPreviewHeaderTabs';
import { pmPreviewHeaderTabs } from '../constants/pmPreviewHeaderTabs';
import { knmPreviewDownloadPassportPdf } from '../actions/knmPreviewActions';
import { feedbackReset, feedbackSetKnmData } from '../../../FeedbackPackage/MainModule/actions/feedbackActions';
import { ROUTE_FEEDBACK } from '../../../CorePackage/MainModule/routes/routes';
import { IFeedbackKnmData } from '../../../FeedbackPackage/MainModule/interfaces/IFeedbackKnmData';
import { IKnmPreview } from '../../../APIPackage/KnmSearchModule/interfaces/IKnmPreview';
import { parseErknmValue } from '../common/parseErknmValue';

interface IKnmHeaderProps {
  activeTab: string;
  setActiveTab: DispatchReact<SetStateAction<PassportTabsEnum>>;
  hasViolation: boolean;
  isErknm?: boolean;
  isPm?: boolean;
  resultOfInspectionPm?: string;
  passportData?: IKnmPreview;
}

export const KnmHeader = memo(
  ({ activeTab, setActiveTab, hasViolation, isErknm, isPm, resultOfInspectionPm, passportData }: IKnmHeaderProps) => {
    const dispatch = useDispatch<Dispatch<RootActions>>();
    const history = useHistory();
    const onExportToPdf = useCallback(() => dispatch(knmPreviewDownloadPassportPdf()), [dispatch]);
    const onClick = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        const { tab } = event.currentTarget.dataset;

        setActiveTab(tab as PassportTabsEnum);
      },
      [setActiveTab],
    );

    const problemsRoute = useCallback(() => {
      dispatch(feedbackReset());
      history.push(ROUTE_FEEDBACK);

      if (isErknm && passportData) {
        const knmFeedbackData: IFeedbackKnmData = {
          id: passportData.id,
          erpId: passportData.erpId,
          status: passportData.status,
          signed: passportData.signed,
          type: passportData.typeName,
          kindKnm: passportData.inspectionErknm?.kindKnm?.value
            ? parseErknmValue(passportData.inspectionErknm.kindKnm.value)?.title
            : undefined,
          approveRequired: passportData?.inspectionErknm?.approve?.approveRequired,
        };

        dispatch(feedbackSetKnmData(knmFeedbackData));
      }
    }, [dispatch, history, passportData]);

    const dropDown = useMemo(
      () => (
        <div className={styles.CustomDropdownButton}>
          <Dropdown
            items={[
              {
                id: 'export-passport-pdf',
                content: <DropdownItem onClick={onExportToPdf}>Экспорт в PDF</DropdownItem>,
              },
              {
                id: 'knmProblem',
                content: <DropdownItem onClick={problemsRoute}>Проблема с КНМ</DropdownItem>,
              },
            ]}
          />
        </div>
      ),
      [onExportToPdf],
    );

    const previewHeaderTabs = isErknm ? (isPm && pmPreviewHeaderTabs) || erknmPreviewHeaderTabs : knmPreviewHeaderTabs;

    const tabs = useMemo(
      () => (
        <>
          <div className={styles.TabsWrapper}>
            {previewHeaderTabs.map(tab => {
              if (tab.name === PassportTabsEnum.VIOLATION && !hasViolation) {
                return null;
              }
              if (isPm && !resultOfInspectionPm && tab.name === PassportTabsEnum.RESULT) {
                return null;
              }

              const tabItemAttributes = {
                ...(activeTab === tab.name && { 'data-active': true }),
              };

              return (
                <button
                  key={tab.name}
                  className={styles.TabItem}
                  data-tab={tab.name}
                  onClick={onClick}
                  type="button"
                  style={{ outline: 'none' }}
                  {...tabItemAttributes}
                >
                  {tab.text}
                </button>
              );
            })}
          </div>
          {dropDown}
        </>
      ),
      [activeTab, dropDown, hasViolation, isPm, onClick, previewHeaderTabs, resultOfInspectionPm],
    );

    return (
      <div className={styles.KnmHeader}>
        <div className={styles.Buttons}>{tabs}</div>
      </div>
    );
  },
);
